import React from 'react';
import { Alert, Grid, MenuItem, TextField, Typography } from '@mui/material';
import type { CustomerOption, E2IConfig, Enquiry, EnquiryInput, EnquiryType, PartyRef } from '../../types/e2i';
import { ENQUIRY_TYPES } from '../../types/e2i';
import { cleanDateInput, dateOrNull, todayIso } from '../../utils/e2i';
import ActivitiesEditor, { ActivityRow } from './ActivitiesEditor';
import CustomerPicker from './CustomerPicker';
import ProjectSearchPicker from './ProjectSearchPicker';

/** The enquiry's fields as typed on screen. */
export interface EnquiryFormState {
  enquiryDate: string;
  quoteRequiredDate: string;
  customer: CustomerOption | null;
  contactPerson: string;
  phone: string;
  email: string;
  project: PartyRef | null;
  enquiryType: EnquiryType;
  subject: string;
  remarks: string;
  activities: ActivityRow[];
}

export const emptyEnquiryForm = (): EnquiryFormState => ({
  enquiryDate: todayIso(),
  quoteRequiredDate: '',
  customer: null,
  contactPerson: '',
  phone: '',
  email: '',
  project: null,
  enquiryType: 'OneTimeCallout',
  subject: '',
  remarks: '',
  activities: [],
});

export const formFromEnquiry = (e: Enquiry): EnquiryFormState => ({
  enquiryDate: String(e.enquiryDate || '').slice(0, 10),
  quoteRequiredDate: e.quoteRequiredDate ? String(e.quoteRequiredDate).slice(0, 10) : '',
  customer: e.customer?.code ? { code: e.customer.code, name: e.customer.name, federalTaxId: e.customer.federalTaxId ?? null } : null,
  contactPerson: e.contactPerson || '',
  phone: e.phone || '',
  email: e.email || '',
  project: e.project?.code ? { code: e.project.code, name: e.project.name } : null,
  enquiryType: e.enquiryType || 'OneTimeCallout',
  subject: e.subject || '',
  remarks: e.remarks || '',
  activities: (e.activities || []).map((a) => ({ activity: a.activity || '', description: a.description || '' })),
});

const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

export const inputFromForm = (f: EnquiryFormState): EnquiryInput => ({
  enquiryDate: dateOrNull(f.enquiryDate) || todayIso(),
  quoteRequiredDate: dateOrNull(f.quoteRequiredDate),
  customerCode: f.customer?.code || '',
  contactPerson: orNull(f.contactPerson),
  phone: orNull(f.phone),
  email: orNull(f.email),
  projectCode: f.project?.code || null,
  enquiryType: f.enquiryType,
  subject: f.subject.trim(),
  remarks: orNull(f.remarks),
  activities: f.activities
    .filter((a) => a.activity.trim() || a.description.trim())
    .map((a, i) => ({ lineNum: i + 1, activity: a.activity.trim() || a.description.trim().slice(0, 200), description: orNull(a.description) })),
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What stops a save (draft) or a post: the server checks the same and more. */
export function enquiryProblems(f: EnquiryFormState, forPosting: boolean): string[] {
  const out: string[] = [];
  if (!f.customer?.code) out.push('Choose the customer');
  if (!f.subject.trim()) out.push('Type the subject');
  if (!dateOrNull(f.enquiryDate)) out.push('The enquiry date is not a date');
  if (f.quoteRequiredDate.trim() && !dateOrNull(f.quoteRequiredDate)) out.push('The quotation due date is not a date');
  if (f.email.trim() && !EMAIL.test(f.email.trim())) out.push('The e-mail address is not valid');
  if (forPosting && !f.project?.code) out.push('Choose the project (main job)');
  return out;
}

interface Props {
  idPrefix: string;
  companyId: number;
  config: E2IConfig | null;
  value: EnquiryFormState;
  onChange: (next: EnquiryFormState) => void;
  disabled?: boolean;
}

/** The enquiry's header, contact and activities. */
const EnquiryForm: React.FC<Props> = ({ idPrefix, companyId, config, value, onChange, disabled }) => {
  const set = <K extends keyof EnquiryFormState>(key: K, v: EnquiryFormState[K]) => onChange({ ...value, [key]: v });
  const noVat = !!config?.vatRegistered && !!value.customer && !value.customer.federalTaxId;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <CustomerPicker id={`${idPrefix}-customer`} companyId={companyId} value={value.customer} onChange={(c) => set('customer', c)} required disabled={disabled} />
      </Grid>
      <Grid item xs={12} md={6}>
        <ProjectSearchPicker
          id={`${idPrefix}-project`}
          companyId={companyId}
          value={value.project}
          onChange={(p) => set('project', p)}
          disabled={disabled}
          helperText="The SAP project the work is for; needed to post the enquiry"
        />
      </Grid>
      {noVat ? (
        <Grid item xs={12}>
          <Alert severity="warning">This customer has no VAT number in SAP. Its invoice requests cannot be booked until SAP has one.</Alert>
        </Grid>
      ) : null}
      <Grid item xs={12} sm={6} md={4}>
        <TextField id={`${idPrefix}-type`} select label="Enquiry type" value={value.enquiryType} onChange={(e) => set('enquiryType', e.target.value as EnquiryType)} fullWidth required disabled={disabled}>
          {ENQUIRY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={6} sm={3} md={4}>
        <TextField
          id={`${idPrefix}-date`}
          type="date"
          label="Enquiry date"
          value={value.enquiryDate}
          onChange={(e) => set('enquiryDate', cleanDateInput(e.target.value))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={6} sm={3} md={4}>
        <TextField
          id={`${idPrefix}-quote-date`}
          type="date"
          label="Quotation due by"
          value={value.quoteRequiredDate}
          onChange={(e) => set('quoteRequiredDate', cleanDateInput(e.target.value))}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField id={`${idPrefix}-subject`} label="Subject" value={value.subject} onChange={(e) => set('subject', e.target.value.slice(0, 254))} fullWidth required disabled={disabled} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField id={`${idPrefix}-contact`} label="Contact person" value={value.contactPerson} onChange={(e) => set('contactPerson', e.target.value.slice(0, 100))} fullWidth disabled={disabled} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField id={`${idPrefix}-phone`} label="Phone" type="tel" value={value.phone} onChange={(e) => set('phone', e.target.value.slice(0, 50))} fullWidth disabled={disabled} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          id={`${idPrefix}-email`}
          label="E-mail"
          type="email"
          value={value.email}
          onChange={(e) => set('email', e.target.value.slice(0, 100))}
          error={!!value.email.trim() && !EMAIL.test(value.email.trim())}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField id={`${idPrefix}-remarks`} label="Remarks" value={value.remarks} onChange={(e) => set('remarks', e.target.value.slice(0, 4000))} multiline minRows={2} fullWidth disabled={disabled} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>Activities</Typography>
        <ActivitiesEditor idPrefix={idPrefix} rows={value.activities} onChange={(rows) => set('activities', rows)} disabled={disabled} />
      </Grid>
    </Grid>
  );
};

export default EnquiryForm;
